import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMask } from "@react-input/mask";
import { FormikProps } from "formik";

import { FormikTextField } from "../formik/FormikTextField";
import { nameof } from "../utils/nameof";
import { WithInsuranceNumberAndBirthday } from "./ClientInsuranceNumberField.types";
import { formatDate, formatInsuranceNumber } from "./ClientInsuranceNumberField.utils";

const MAX_MIN_CHAR_LENGTH = "10(4)";
const INSURANCE_NUMBER_FIELD_NAME = "insuranceNumber";
const BIRTHDAY_FIELD_NAME = "birthday";

interface ClientInsuranceNumberFieldProps<T extends WithInsuranceNumberAndBirthday> {
  formik: FormikProps<T>;
  optional?: boolean;
}

export function ClientInsuranceNumberField<T extends WithInsuranceNumberAndBirthday>({
  formik,
  optional,
}: ClientInsuranceNumberFieldProps<T>) {
  const { t } = useTranslation();
  const normalizedLength = (formik.values.insuranceNumber ?? "").length;

  const maskRef = useMask({
    mask: "SSSS BBBBBB",
    replacement: { S: /[\dXx]/, B: /\d/ },
  });

  const [isFocused, setIsFocused] = useState(false);

  const displayValue = formatInsuranceNumber(formik.values.insuranceNumber);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void formik.setFieldValue(INSURANCE_NUMBER_FIELD_NAME, e.target.value.replace(/\s/g, "") || null);
  };

  const changeBirthdayValue = () => {
    if (formik.values.birthday) {
      return;
    }

    const insuranceNumber = formik.values.insuranceNumber;

    if (insuranceNumber?.length === 10) {
      const datePart = insuranceNumber.slice(4);
      const birthday: Date | null = formatDate(datePart);

      if (birthday === null) {
        return;
      }

      void formik.setFieldValue(nameof<WithInsuranceNumberAndBirthday>(BIRTHDAY_FIELD_NAME), birthday);
      const trimmed = insuranceNumber.slice(0, 4);
      void formik.setFieldValue(INSURANCE_NUMBER_FIELD_NAME, trimmed);
    }
  };

  return (
    <FormikTextField
      formik={formik}
      inputRef={maskRef}
      fieldName={INSURANCE_NUMBER_FIELD_NAME}
      label={t("clients.create.insuranceNumber")}
      placeholder="xxxx xxxxxx"
      optional={optional}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        formik.handleBlur(e);
        changeBirthdayValue();
      }}
      value={displayValue}
      helperText={isFocused ? `${normalizedLength}/${MAX_MIN_CHAR_LENGTH}` : undefined}
    />
  );
}
